<template>

  <LoadingDiv v-if="!isReady" />

  <div v-else class="encounter-detail-workspace">
    <div v-if="encounter">
      <div class="section">
        <div class="encounter-create-workstation">
          <section class="encounter-patient-summary" aria-label="Patient details">
            <div class="encounter-patient-copy">
              <div class="encounter-patient-context"><span>{{ encounter.scheme?.name }}</span><small>{{ encounter.client_name }}</small></div>
              <h2>{{ encounter.name }} <small>{{ showAgeSex(encounter?.dob, encounter?.sex) }}</small></h2>
              <p class="encounter-patient-id">{{ encounter.enrollee_number }}</p>
              <div class="encounter-patient-date" v-if="encounterDate || encounterRegister">
                <span><i class="material-icons" aria-hidden="true">event</i>{{ new Date(encounterDate || encounterRegister).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) }}</span>
                <button type="button" class="encounter-card-action" title="Change encounter date" aria-label="Change encounter date" @click="handleDateReset()" v-if="!encounter?.encounter_date"><i class="material-icons" aria-hidden="true">edit</i></button>
              </div>
            </div>
            <div class="encounter-patient-actions">
              <img v-if="encounter.profile_img" :src="encounter.profile_img" alt="" class="encounter-patient-avatar" />
              <span v-else class="encounter-patient-avatar encounter-patient-initial" aria-hidden="true">{{ String(encounter.name || '?').trim().charAt(0).toUpperCase() }}</span>
              <button type="button" class="encounter-patient-change" @click="handleRestart()"><i class="material-icons" aria-hidden="true">close</i><span>Change patient</span></button>
            </div>
          </section>
          <!-- Encounter Starts: Date Form -->
          <template v-if="!encounterDate || !encounterRegister">
            <section class="encounter-detail-card encounter-date-card encounter-visit-date-picker" :class="{ 'has-error': error }" v-if="!encounterRegister">
              <h3>Set encounter date</h3>
              <p>Choose the day of this patient’s visit to continue.</p>
              <div class="encounter-visit-date-field">
                <label for="encounterDate">Day of visit</label>
                <input id="encounterDate" type="text" v-model="encounterDate" class="datepicker validate browser-default" required />
              </div>
            </section>
            <div class="card encounter-detail-card encounter-action-card" v-else>
            <div class="card-panel encounter-action-card-content" :class="{ 'red lighten-5': error }">
              <div class="encounter-visit-actions">
                <button type="button"
                  class="waves-effect encounter-visit-button encounter-visit-cancel"
                  :disabled="isSubmitting" @click.prevent="handleRestart()">
                  <i class="material-icons" aria-hidden="true">close</i>
                  <span>Cancel</span>
                </button>

                <div class="encounter-visit-primary-actions">
                  <button type="button"
                    class="waves-effect waves-light encounter-visit-button encounter-visit-save"
                    :disabled="isSubmitting" @click.prevent="handleEncounterNext('save')">
                    <i class="material-icons" aria-hidden="true">save</i>
                    <span>Save Visit</span>
                  </button>
                  <button type="button"
                    class="waves-effect waves-light encounter-visit-button encounter-visit-auth"
                    :disabled="isSubmitting" @click.prevent="handleEncounterNext('auth')">
                    <span>Continue to Auth</span>
                    <i class="material-icons" aria-hidden="true">arrow_forward</i>
                  </button>
                </div>
              </div>
            </div>
            </div>
            <!-- Sumbmitting -->
            <div class="progress" v-if="isSubmitting">
              <div class="indeterminate"></div>
            </div>
          </template>

          <!-- Encounter Details -->
          <template v-else>
            <!-- Check for active encoounter -->
            <div class="progress" v-if="isLoading">
              <div class="indeterminate"></div>
            </div>
            <!-- Entire Provider (diagnosis, claims) Display starts here -->
            <div ref="encounterCareCard" class="card grey lighten-4 encounter-detail-card encounter-care-card" v-else>

              <div id="claim"></div>

              <!-- Display Diagnosis -->
              <section class="encounter-diagnosis-summary" v-if="diagnosisList.length">
                    <div>
                      <span class="encounter-diagnosis-label">Diagnosis</span>
                      <p>
                        <span v-if="encounter?.diagnosis">
                          {{ encounter?.diagnosis === diagnosisList.join(", ") ? diagnosisList.join(", ") :
                            `(${encounter?.diagnosis}) ${diagnosisList.join(", ")}` }}
                        </span>
                        <span v-else>
                          {{ diagnosisList.join(", ") }}
                        </span>
                      </p>
                    </div>
                    <button type="button" class="encounter-card-action" aria-label="Edit diagnosis" @click="handleReviewDiagnosis"><i class="material-icons" aria-hidden="true">edit</i></button>
              </section>

              <EncounterMessages v-if="showEncounterWorkspace" ref="encounterMessages"
                :encounter-id="encounter?.encounter_date_set ? encounter.id : null"
                :initial-messages="initialEncounterMessages"
                :initially-expanded="messagesInitiallyExpanded !== false"
                :expanded="activeWorkspacePanel === 'messages'"
                show-body-close
                :draft-mode="bundleMessageWithRequest" v-model:draft="messageDraft"
                @sent="handleMessageSent" @toggle="toggleWorkspacePanel('messages')" />

              <section class="encounter-request-section" :class="{ 'is-workspace-ready': showEncounterWorkspace }">
                <header class="encounter-request-head" v-if="showEncounterWorkspace">
                  <button type="button" class="encounter-request-toggle"
                    :aria-expanded="requestPanelExpanded"
                    aria-controls="encounter-authorization-request-panel"
                    @click="toggleWorkspacePanel('request')">
                    <span class="encounter-request-title">
                      <span class="encounter-request-icon"><i class="material-icons">playlist_add_check</i></span>
                      <span class="encounter-request-copy">
                        <strong>Authorization Request</strong>
                        <small>Add and review encounter items</small>
                      </span>
                    </span>
                    <i class="material-icons encounter-request-chevron"
                      :title="requestPanelExpanded ? 'Collapse authorization request' : 'Expand authorization request'">
                      {{ requestPanelExpanded ? "expand_less" : "expand_more" }}
                    </i>
                  </button>
                </header>

                <div id="encounter-authorization-request-panel" class="encounter-request-body"
                  :class="{ 'is-expanded': requestPanelExpanded }"
                  :aria-hidden="!requestPanelExpanded" :inert="requestPanelExpanded ? null : ''">
                  <div class="encounter-request-body-inner">
                    <div class="encounter-item-stack">
              <!-- Claims Display Card -->
              <div ref="encounterItemsCard" class="card encounter-items-card" v-if="claimsDisplayCard">
                <div class="card-content">
                  <span class="card-title">Item(s)</span>

                  <div class="encounter-selected-items-box">
                  <!-- List claims -->
                  <ol id="item-list" class="collection">
                    <li class="collection-item" v-for="claim in claims" :key="claim.id">
                      <span class="encounter-item-copy">
                        <b>{{ claim.claim }}</b> x {{ claim.qty }} (â‚¦{{
                          claim.amt
                            .toString()
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                        }}) <span class="chip blue lighten-5 pointable" v-if="claim.amt == 0"
                          title="capitated item">Cap</span>
                      </span>

                      <a href="#" class="secondary-content cancel-item" @click.prevent="handleDelete(claim)"
                        title="delete"><i class="material-icons grey-text">cancel</i></a>

                      <a href="#" class="secondary-content edit-item" @click.prevent="handleEdit(claim)" title="edit"><i
                          class="material-icons" :class="{
                            'grey-text': editId != claim.id,
                            'blue-text': editId == claim.id,
                          }">edit</i></a>
                    </li>
                  </ol>

                    <!-- Total -->
                    <ul id="item-total-list" class="collection encounter-selected-total-list" v-if="claimsSubmitCard">
                      <li class="collection-item pull-right">
                        <b>Total</b> (Sent Amount):
                        <b><span class="red-text">â‚¦{{ totalSent(claimsList) }}</span></b>
                      </li>
                    </ul>
                  </div>

                  <!-- Claims Submit -->
                  <div v-if="claimsSubmitCard">
                    <div id="edit"></div>

                    <br />

                    <div class="encounter-submit-actions">
                      <div class="encounter-secondary-actions">
                        <button type="button" class="encounter-action-button encounter-action-add"
                          :class="{ disabled: isSubmitting }" @click.prevent="handleNext('moreClaims')">
                          <i class="material-icons">add</i>
                          <span>Add</span>
                        </button>

                        <button type="button" class="encounter-action-button encounter-action-clear"
                          :class="{ disabled: isSubmitting }" @click.prevent="handleClearSelectedItems">
                          <i class="material-icons">clear</i>
                          <span>Clear</span>
                        </button>
                      </div>

                      <button type="button" class="encounter-action-button encounter-action-submit modal-trigger" :class="{
                        disabled: isSubmitting,
                      }" data-target="confirmSubmit">
                        <i class="material-icons">send</i>
                        <span>{{ isSubmitting ? "Submitting ..." : "Submit" }}</span>
                      </button>
                    </div>

                  </div>
                </div>

                <!-- Submission progress is anchored just below the action-card divider. -->
                <div class="progress encounter-submit-progress" v-if="isSubmitting">
                  <div class="indeterminate"></div>
                </div>
              </div>

              <!-- Add claim -->
              <!-- <AddClaim :enrollee="encounter" :type="type" :next="next" :reload="reload" :editDiagnosis="resetDiagnosis"
                :claimItem="claim" :level="level" @update="handleUpdate" @clear="handleClear" /> -->

            <AddClaim :turl="{ sid: encounter?.scheme_id, pid: encounter?.provider_id }" :type="type" :next="next" :reload="reload" :editDiagnosis="resetDiagnosis" :claimItem="claim"
                :cancelDiagnosisToHome="!hasEncounterDiagnosis"
                :confirmMissingDiagnosis="requiresDiagnosisConfirmation"
                :persistDiagnosis="encounter?.encounter_date_set ? handleDiagnosisSave : null"
                @update="handleUpdate" @clear="handleClear" @item-added="handleItemAdded"
                @diagnosis-ready="handleDiagnosisReady" />

              <!-- Approvals -->
              <div class="card encounter-approvals-card" v-if="encounter?.claims?.length">
                <ol class="collection">
                  <template v-for="claim in encounter.claims.slice().reverse()" :key="claim.id">

                    <!-- approved -->
                    <template v-if="claim.authorization == 'y'">
                      <li class="collection-item"
                        :class="{ 'grey lighten-2': claim.sent_price == 0, 'green lighten-4': claim.sent_price != 0 }"
                        v-if="claim.sent_price === claim.approved_price">
                        <span class="encounter-item-copy">
                          <b>{{ claim.item }}</b> x {{ claim.sent_qty }}
                          ({{
                            toMoney(claim.sent_price)
                          }}) <span v-if="claim.sent_price != 0">-> (Approved)</span> ->
                          <span class="blue-text tooltipped pointable" data-position="top" :data-tooltip="claim.comment"
                            v-if="claim.comment">({{
                              claim.authorization_code }})</span>
                          <span class="black-text" v-else>({{ claim.authorization_code }})</span>
                        </span>

                        <span class="secondary-content pointable"
                          :class="{ 'black-text': claim.sent_price == 0, 'green-text': claim.sent_price != 0 }"
                          :title="claim.sent_price == 0 ? 'Completed' : 'Approved'">
                          <i class="material-icons" aria-hidden="true">check_circle</i><span>{{ claim.sent_price == 0 ? 'Completed' : 'Approved' }}</span>
                        </span>
                      </li>
                      <li class="collection-item blue lighten-4" v-else>
                        <span class="encounter-item-copy">
                          <b>{{ claim.item }}</b> x {{ claim.sent_qty }} (Charged:
                          {{
                            toMoney(claim.sent_price)
                          }}) -> x {{ claim.approved_qty }} (Approved: <b>{{ toMoney(claim.approved_price) }}</b>) ->
                          <span class="blue-text text-darken-4 tooltipped pointable" data-position="top"
                            :data-tooltip="claim.comment" v-if="claim.comment">({{
                              claim.authorization_code }})</span>
                          <span class="black-text" v-else>({{ claim.authorization_code }})</span>
                        </span>

                        <span class="secondary-content green-text pointable" title="Approved">
                          <i class="material-icons" aria-hidden="true">check_circle</i><span>Approved</span>
                        </span>
                      </li>
                    </template>

                    <!-- pending -->
                    <li class="collection-item lime lighten-4 pointable"
                      v-if="claim.authorization == 'z' && claim.status == 'a'" title="pending request">
                      <span class="encounter-item-copy">
                        <b>{{ claim.item }}</b> x {{ claim.sent_qty }} (Charged:
                        {{
                          toMoney(claim.sent_price)
                        }})
                      </span>

                      <span class="secondary-content orange-text pointable" title="Pending">
                        <i class="material-icons" aria-hidden="true">watch_later</i><span>Pending</span>
                      </span>
                    </li>

                    <!-- primary care -->
                    <li class="collection-item white care-primary-care-row is-readonly"
                      v-if="claim.authorization == 'n' && claim.status == 'a'" title="Primary care">
                      <span class="encounter-item-copy">
                        <b>{{ claim.item }}</b> x {{ claim.sent_qty }} (Charged:
                        {{
                          toMoney(claim.sent_price)
                        }})
                      </span>

                      <span class="secondary-content care-primary-care-chip" title="Primary care">
                        <span>Primary care</span>
                      </span>
                    </li>

                    <!-- declined -->
                    <li class="collection-item red lighten-4" v-if="claim.authorization == 'z' && claim.status == 'd'">
                      <s class="encounter-item-copy">
                        <span class="tooltipped pointable" data-position="top" :data-tooltip="claim.comment"
                          v-if="claim.comment">
                          <b>{{ claim.item }}</b> x
                          {{ claim.sent_qty }} (Charged: {{
                            toMoney(claim.sent_price)
                          }})
                        </span>

                        <span v-else>
                          <b>{{ claim.item }}</b> x
                          {{ claim.sent_qty }} (Charged: {{
                            toMoney(claim.sent_price)
                          }})
                        </span>
                      </s>

                      <span class="secondary-content red-text pointable" title="Declined"><i
                          class="material-icons" aria-hidden="true">cancel</i><span>Declined</span></span>
                    </li>
                  </template>
                </ol>
              </div>
                    </div>
                  </div>
                </div>
              </section>

            </div>
          </template>

          <Teleport to="body">
            <div class="encounter-modal-host">
              <Modal id="confirmSubmit" class="modal encounter-confirm-modal" :title="alertTitle" :message="alertMessage"
                @confirm="handleConfirm" />
            </div>
          </Teleport>

        </div>
      </div>
    </div>

    <div class="section center" v-if="!encounter">
      <h6>No Encounter Record for Enrollee <em>found</em>...</h6>
    </div>
  </div>
</template>

<script>
import { ref, onBeforeUnmount, onMounted, onUpdated, watch, watchEffect, computed, nextTick } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { toMoneyFx } from "@/composables/calFunction/cal.js";
import { ageSex } from "@/composables/fxLibrary/coreFx";
import postItem from "@/composables/postItem";
import LoadingDiv from "@/components/LoadingDiv.vue";
import AddClaim from "@/components/claim/AddClaimComponent.vue";
import { getMinDate, getMaxDate } from "@/composables/encounter/eligible";
import { checkEnrolleeHistory } from "@/composables/serverCaller/singleEncounterHistory";
import { saveDiagnosis } from "@/composables/serverCaller/serverCaller";
import Modal from "@/components/alerts/ModalComponent.vue";
import EncounterMessages from "@/components/encounter/EncounterMessagesComponent.vue";
import nativeUi from "@/ui/native-ui";


export default {
  name: "EnrolleeEncounterComponent",
  props: ["enrollee", "serverDate", "searchPeriod", "messagesInitiallyExpanded"],
  emits: ["restart", "refresh"],
  components: { LoadingDiv, AddClaim, Modal, EncounterMessages },
  setup(props, context) {
    // settings
    const SETTINGS_VISIT = props.enrollee.scheme_id === 1 ? true : false;
    // others
    const response = ref(null);
    const selectedItem = ref(null);
    const isReady = ref(true);
    const isLoading = ref(false);
    const isSubmitting = ref(false);
    const alertTitle = ref("Confirm");
    const alertMessage = ref("Are you sure? Please confirm that all the details provided are accurate. Once submitted, changes cannot be made.");
    const error = ref(false);
    const encounterDate = ref(null);
    const encounterRegister = ref(null);
    const encounterHistory = ref(null);
    const editId = ref(null);
    const editClaim = ref(null);
    const deleteClaim = ref(null);
    const claimsDisplayCard = ref(false);
    const encounterItemsCard = ref(null);
    const encounterCareCard = ref(null);
    const claimsSubmitCard = ref(false);
    const diagnosisList = ref([]);
    const resetDiagnosis = ref(false);
    const type = ref("auth"); // close claim after input
    const next = ref("next");
    const claimsList = ref([]);
    const claim = ref(null);
    const reload = ref(null);
    const encounterMessages = ref(null);
    const messageDraft = ref("");
    const messageClientMessageId = ref(null);
    const activeWorkspacePanel = ref(props.messagesInitiallyExpanded !== false ? "messages" : "request");


    // vue router
    const router = useRouter();

    // store
    const store = useStore();

    // user id
    const userId = store.state.user?.id;

    // user level
    const level = store.state.user?.level;

    // config
    const providerId = store.state?.user?.account?.id;

    const hasEncounterDiagnosis = computed(() => {
      const currentDiagnosis = String(encounter.value?.diagnosis || "").trim();
      const selectedDiagnosis = diagnosisList.value.some(item => String(item || "").trim());

      return Boolean(currentDiagnosis || selectedDiagnosis);
    });

    const requiresDiagnosisConfirmation = computed(() => Boolean(
      encounter.value?.id
      && encounter.value?.encounter_date_set
      && !String(encounter.value?.diagnosis || "").trim()
    ));

    const showEncounterWorkspace = computed(() => {
      return Boolean(encounterDate.value && encounterRegister.value && hasEncounterDiagnosis.value);
    });

    const initialEncounterMessages = computed(() => {
      const messages = encounter.value?.stores?.encounter_messages;
      return Array.isArray(messages) ? messages : [];
    });

    const requestPanelExpanded = computed(() => {
      return !showEncounterWorkspace.value || activeWorkspacePanel.value === "request";
    });

    const toggleWorkspacePanel = panel => {
      if (!showEncounterWorkspace.value) return;

      activeWorkspacePanel.value = activeWorkspacePanel.value === panel
        ? (panel === "messages" ? "request" : "messages")
        : panel;
    };

    watch(() => props.messagesInitiallyExpanded, value => {
      activeWorkspacePanel.value = value !== false ? "messages" : "request";
    });

    const hasAuthorizationActivity = computed(() => {
      return (encounter.value?.claims || []).some(claimItem => ["y", "z"].includes(claimItem?.authorization));
    });

    const hasSelectedAuthorizationRequest = computed(() => {
      return claimsList.value.some(claimItem => Number(claimItem?.amt) > 0);
    });

    const bundleMessageWithRequest = computed(() => {
      return !hasAuthorizationActivity.value || hasSelectedAuthorizationRequest.value;
    });

    const newMessageClientId = () => {
      if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
      }

      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, character => {
        const random = Math.random() * 16 | 0;
        const value = character === "x" ? random : (random & 0x3 | 0x8);
        return value.toString(16);
      });
    };

    const bundledMessage = () => {
      const body = messageDraft.value.trim();

      if (!body || !bundleMessageWithRequest.value) {
        return null;
      }

      if (!messageClientMessageId.value) {
        messageClientMessageId.value = newMessageClientId();
      }

      return {
        body,
        client_message_id: messageClientMessageId.value,
      };
    };

    const handleMessageSent = (payload) => {
      if (!response.value || !Array.isArray(payload?.messages)) {
        return;
      }

      response.value = {
        ...response.value,
        stores: {
          ...(response.value.stores || {}),
          encounter_messages: [...payload.messages],
        },
      };
      context.emit("refresh", response.value);
    };

    // toolTip
    const toolTip = () => {
      var elems = document.querySelectorAll(".tooltipped");
      nativeUi.Tooltip.init(elems, {});
    };

    // restart toolTip
    const restartComments = () => {
      // load comment
      setTimeout(() => {
        toolTip();
      }, 2000);
    };

    // load after data loads and updates
    watchEffect(() => {
      if (response.value) {
        restartComments();
      }
    });

    // add up
    const totalSent = (claims) => {
      let total = 0;
      claims.forEach((x) => {
        total = Number(total) + Number(x.amt);
      });
      return total
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    // to Money
    const toMoney = (val) => {
      return toMoneyFx(val);
    }

    // ageSex
    const showAgeSex = (dob, sex) => {
      const { fx } = ageSex(dob, sex);
      return fx.value;
    };

    // scrollbar
    const resetScrollBar = () => {
      // Restore scrollbar (insurance)
      document.body.style.overflow = 'auto';
    };

    // loader
    const encounterStarter = (val) => {
      // get started
      response.value = val;
      // set provider immediately
      response.value.provider_id = providerId;
      // check if it's a reload
      if ('diagnosis' in val) {
        // reload diagnosis
        reload.value = response.value?.diagnosis;
      }
      if ('encounter_date' in val) {
        // reload encounter date
        encounterDate.value = response.value?.encounter_date;
        // pass register lock
        encounterRegister.value = encounterDate.value;
      }
    }

    // starter
    onMounted(() => {
      // start
      encounterStarter(props.enrollee);
    });

    // alert
    onMounted(() => {
      // modal
      const elems = document.querySelectorAll(".modal");
      nativeUi.Modal.init(elems, {});

      // only when dtepicker is required
      if (!props.serverDate || !props.searchPeriod) return;

      // activate date
      const elemsDate = document.querySelectorAll(".datepicker");
      const minDate = getMinDate(props.serverDate, props.searchPeriod, props.enrollee);
      const maxDate = getMaxDate(props.serverDate, props.searchPeriod, props.enrollee);

      nativeUi.Datepicker.init(elemsDate, {
        container: document.body,
        minDate: minDate,
        maxDate: maxDate,
        onSelect: function (date) {

          let newDate = new Date(date);
          if (SETTINGS_VISIT) {
            encounterRegister.value = newDate
              .toLocaleDateString("en-GB")
              .split("/")
              .reverse()
              .join("-");
          } else {
            encounterDate.value = newDate
              .toLocaleDateString("en-GB")
              .split("/")
              .reverse()
              .join("-");
            // ensure next
            encounterRegister.value = encounterDate.value;
          }

        },

      });

    });

    // alert
    onUpdated(() => {
      // modal
      const elems = document.querySelectorAll(".modal");
      nativeUi.Modal.init(elems, {});

      // only when dtepicker is required
      if (!props.serverDate || !props.searchPeriod) return;

      // activate date
      const elemsDate = document.querySelectorAll(".datepicker");
      const minDate = getMinDate(props.serverDate, props.searchPeriod, props.enrollee);
      const maxDate = getMaxDate(props.serverDate, props.searchPeriod, props.enrollee);

      nativeUi.Datepicker.init(elemsDate, {
        container: document.body,
        minDate: minDate,
        maxDate: maxDate,
        onSelect: function (date) {

          let newDate = new Date(date);
          if (SETTINGS_VISIT) {
            encounterRegister.value = newDate
              .toLocaleDateString("en-GB")
              .split("/")
              .reverse()
              .join("-");
          } else {
            encounterDate.value = newDate
              .toLocaleDateString("en-GB")
              .split("/")
              .reverse()
              .join("-");
            // ensure next
            encounterRegister.value = encounterDate.value;
          }

        },

      });

    });

    // handle Continue
    const handleEncounterNext = (val) => {
      if (val === "auth") {
        // load encounter Date
        encounterDate.value = encounterRegister.value;
      } else if (val === "save") {
        // call submit
        handleSubmit();
      }
    }

    // watch updates
    watchEffect(() => {
      if (encounterDate.value) {
        // release window
        // insurance
        resetScrollBar();
      }
    });

    // handle date reset
    const handleDateReset = () => {
      // clear
      encounterDate.value = null;
      // clear register
      encounterRegister.value = null;
      // clear loader
      isLoading.value = false;
    }

    // handle Emitted update
    const handleUpdate = (val) => {
      claimsDisplayCard.value = val.claimsDisplayCard;
      claimsSubmitCard.value = val.claimsSubmitCard;
      claimsList.value = [...val.claimsList];
      diagnosisList.value = [...val.diagnosisList];
    };

    const handleItemAdded = async () => {
      await nextTick();

      window.requestAnimationFrame(() => {
        encounterItemsCard.value?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    };

    let diagnosisScrollTimer = null;

    const hasVisibleDatepicker = () => {
      return Array.from(document.querySelectorAll(".datepicker-modal")).some(element => {
        const style = window.getComputedStyle(element);
        return style.display !== "none" && style.visibility !== "hidden" && Number(style.opacity) !== 0;
      });
    };

    const scrollDiagnosisIntoView = (diagnosisCardElement, attempt = 0) => {
      window.clearTimeout(diagnosisScrollTimer);

      diagnosisScrollTimer = window.setTimeout(() => {
        const pageIsSettling = isLoading.value || hasVisibleDatepicker();

        if ((!diagnosisCardElement?.isConnected || pageIsSettling) && attempt < 30) {
          scrollDiagnosisIntoView(diagnosisCardElement, attempt + 1);
          return;
        }

        if (!diagnosisCardElement?.isConnected) {
          return;
        }

        window.requestAnimationFrame(() => {
          const diagnosisTop = diagnosisCardElement.getBoundingClientRect().top + window.scrollY;

          window.scrollTo({
            top: Math.max(0, diagnosisTop - 210),
            behavior: "smooth",
          });
        });
      }, attempt === 0 ? 400 : 100);
    };

    const handleDiagnosisReady = diagnosisCardElement => {
      if (!encounterDate.value || !encounterRegister.value || hasEncounterDiagnosis.value) {
        return;
      }

      scrollDiagnosisIntoView(diagnosisCardElement);
    };

    watch([encounterDate, encounterRegister, isLoading], async () => {
      if (!encounterDate.value || !encounterRegister.value || isLoading.value || hasEncounterDiagnosis.value) {
        return;
      }

      await nextTick();
      handleDiagnosisReady(encounterCareCard.value?.querySelector(".claim-collection-add-card"));
    }, { flush: "post" });

    onBeforeUnmount(() => {
      window.clearTimeout(diagnosisScrollTimer);
    });

    const handleDiagnosisSave = async (diagnosis) => {
      const savedEncounter = await saveDiagnosis(encounter.value, diagnosis);

      if (!savedEncounter) {
        return null;
      }

      response.value.diagnosis = savedEncounter.diagnosis;
      response.value.stores = savedEncounter.stores;
      reload.value = savedEncounter.diagnosis;
      context.emit("refresh", response.value);

      return savedEncounter;
    };

    // handle edit
    const handleEdit = (val) => {
      claim.value = { action: "edit", claim: val };
      editId.value = val.id;
      // top
      router.push({ hash: "#edit" });
    };

    // handle edit
    const handleDelete = (val) => {
      claim.value = { action: "delete", claim: val };
    };

    // handle remove (passed as props)
    const handleReviewDiagnosis = () => {
      activeWorkspacePanel.value = "request";
      resetDiagnosis.value = true;
    };

    // handle next
    const handleNext = (val) => {
      if (val === "moreClaims" && !hasEncounterDiagnosis.value) {
        handleReviewDiagnosis();
        return;
      }

      next.value = val;
    };

    // clear selected claim items
    const handleClearSelectedItems = () => {
      if (isSubmitting.value) {
        return;
      }

      claimsDisplayCard.value = false;
      claimsSubmitCard.value = false;
      claimsList.value = [];
      editId.value = null;
      claim.value = { action: "clear", nonce: Date.now() };
      next.value = "claims";

      router.push({ hash: "#claim" });
    };

    // handle clear (emiited)
    const handleClear = (val) => {
      if (val.id == "resetDiagnosis") {
        resetDiagnosis.value = val.value;
      } else if (val.id == "claim") {
        claim.value = val.value;
      } else if (val.id == "next") {
        next.value = val.value;
      } else if (val.id == "editId") {
        editId.value = val.value;
      } else if (val.id == "restartEncounter") {
        handleRestart();
        return;
      }
      // top
      router.push({ hash: "#claim" });
    };

    // hanlde restart
    const handleRestart = (updatedEncounter = null) => {
      context.emit("restart", updatedEncounter);
    };

    const completeAuthorizationSubmit = (result) => {
      messageDraft.value = "";
      messageClientMessageId.value = null;
      isSubmitting.value = false;
      handleRestart(result?.data || null);
      resetScrollBar();
    };

    // confirmation
    const handleConfirm = (val) => {
      // call submit if confirmed
      if (val) {
        handleSubmit();
      }
    }

    // handle final submit
    const handleUpdateSubmit = async () => {

      // start
      isSubmitting.value = true;

      // provier
      response.value.provider_id = providerId;
      // add encounter date
      response.value.encounter_date = encounterDate.value; // (goes into encounter)

      let keysToRemove = ["scheme", "created_at", "updated_at", "diagnosis", "stores"];
      let newEncounter = {
        ...Object.fromEntries(
          Object.entries(encounter.value).filter(
            ([key]) => !keysToRemove.includes(key)
          )
        ),
      };
      newEncounter = {
        ...newEncounter,
        user_id: userId,
      };

      // setup claims
      let newClaims = [];

      // auth checker
      let authorization_status;
      let authorization_code;

      // load
      claims.value.forEach((x) => {
        if (x.amt == 0) {
          authorization_status = "y";
          authorization_code = "Capitation";
        } else {
          authorization_status = "z";
          authorization_code = null;
        }

        newClaims.push({
          encounter_id: null,
          item: x.claim,
          sent_qty: x.qty,
          sent_price: x.amt,
          approved_qty: x.qty,
          approved_price: x.amt,
          standard_price: x.standard,
          authorization: authorization_status,
          authorization_code: authorization_code,
          user_id: userId,
        });
      });

      // put all together
      let newVal = {
        encounter: newEncounter,
        claims: newClaims,
        allow_submitted_encounter: true,
      };

      const message = bundledMessage();
      if (message) {
        newVal.message = message;
      }

      // post item
      const { res, err } = await postItem("claim/create/bulk", newVal);

      if (res.value) {

        if (res.value.status) {
          completeAuthorizationSubmit(res.value);


        } else {
          // claims month has closed
          error.value = "Bills for enrollee Month has been submitted!";
          isSubmitting.value = false; // end

          // reset on fail
          resetScrollBar();
        }


      } else {

        // manage error
        error.value = err.value;
        isSubmitting.value = false; // end

        // reset on fail
        resetScrollBar();

      }
    };

    // handle final submit
    const handleSubmit = async () => {

      // main check
      if (encounter.value?.encounter_date_set) {

        //update here
        handleUpdateSubmit();

      } else {

        // real submit
        // start
        isSubmitting.value = true;

        // provier
        response.value.provider_id = providerId;
        // add encounter date
        response.value.encounter_date = encounterDate.value ? encounterDate.value : encounterRegister.value; // (goes into encounter)

        // setup encounter
        let diagnosis = diagnosisList.value.join(", ");
        let keysToRemove = ["id", "encounter", "plan", "scheme", "created_at", "updated_at"];
        let newEncounter = {
          ...Object.fromEntries(
            Object.entries(encounter.value).filter(
              ([key]) => !keysToRemove.includes(key)
            )
          ),
        };
        newEncounter = {
          ...newEncounter,
          encounter_cluster_id: null,
          diagnosis: diagnosis,
          user_id: userId,
        };

        // setup claims
        let newClaims = [];

        // auth checker
        let authorization_status;
        let authorization_code;

        // load
        claims.value.forEach((x) => {
          if (x.amt == 0) {
            authorization_status = "y";
            authorization_code = "Capitation";
          } else {
            authorization_status = "z";
            authorization_code = null;
          }

          newClaims.push({
            encounter_id: null,
            item: x.claim,
            sent_qty: x.qty,
            sent_price: x.amt,
            approved_qty: x.qty,
            approved_price: x.amt,
            standard_price: x.standard,
            authorization: authorization_status,
            authorization_code: authorization_code,
            user_id: userId,
          });
        });

        // put all together
        let newVal = { encounter: newEncounter, claims: newClaims };
        const message = bundledMessage();
        if (message) {
          newVal.message = message;
        }

        // post item
        const { res, err } = await postItem("claim/encounter/bulk", newVal);

        if (res.value) {


          if (res.value.status) {
            completeAuthorizationSubmit(res.value);

          } else {
            // claims month has closed
            error.value = "Bills for enrollee Month has been submitted!";
            isSubmitting.value = false; // end

            // reset on fail
            resetScrollBar();
          }

        } else {

          // manage errors
          error.value = err.value;
          isSubmitting.value = false; // end

          // reset on fail
          resetScrollBar();

        }

      }
    };

    // // watch encounter selection
    watchEffect(() => {
      if (encounterRegister.value && !('encounter_date' in props.enrollee) && !encounterHistory.value) {

        (async () => {
          // start laoding immediately
        isLoading.value = true;
        // check enrollee history
        let historyRes = await checkEnrolleeHistory(encounter.value, encounterRegister.value);
        if (historyRes?.status) {
          if (historyRes?.val) {
            // set encounterHistory
            encounterHistory.value = historyRes.val;
            // restart
            encounterStarter(encounterHistory.value);
          }
        } else {
          // clear
          // it should atleast
          // return original enrollee detail
          handleRestart();
        }
        // end
        isLoading.value = false;
        })();
      }

    });

    // output encounter results
    const encounter = computed(() => {
      if (!response.value) {
        return null;
      } else {
        return response.value;
      }
    });

    const refreshMessagesFromServer = () => {
      return encounterMessages.value?.refreshFromServer?.() || false;
    };

    context.expose({ refreshMessagesFromServer });

    // output claims results
    const claims = computed(() => {
      if (!claimsList.value) {
        return [];
      } else {
        return claimsList.value;
      }
    });

    return {
      encounterRegister,
      level,
      toMoney,
      showAgeSex,
      handleEncounterNext,
      handleDateReset,
      handleEdit,
      handleReviewDiagnosis,
      handleDelete,
      handleUpdate,
      handleItemAdded,
      handleDiagnosisReady,
      handleDiagnosisSave,
      requiresDiagnosisConfirmation,
      handleNext,
      handleClearSelectedItems,
      handleRestart,
      handleConfirm,
      handleSubmit,
      handleClear,
      alertTitle,
      alertMessage,
      selectedItem,
      totalSent,
      isReady,
      isLoading,
      isSubmitting,
      error,
      encounterDate,
      editId,
      claimsDisplayCard,
      encounterCareCard,
      encounterItemsCard,
      claimsSubmitCard,
      resetDiagnosis,
      diagnosisList,
      reload,
      editClaim,
      deleteClaim,
      encounter,
      encounterMessages,
      messageDraft,
      initialEncounterMessages,
      handleMessageSent,
      activeWorkspacePanel,
      showEncounterWorkspace,
      requestPanelExpanded,
      toggleWorkspacePanel,
      bundleMessageWithRequest,
      claimsList,
      next,
      type,
      claim,
      claims,
    };
  },
};
</script>

<style scoped>
@layer components {
  .encounter-detail-workspace .encounter-patient-summary { align-items: flex-start; background: var(--ui-card); border: 1px solid var(--ui-border); border-radius: var(--ui-radius); box-sizing: border-box; display: flex; gap: 1.5rem; justify-content: space-between; padding: 1.5rem; width: 100%; }
  .encounter-patient-copy { min-width: 0; }
  .encounter-patient-context { align-items: center; display: flex; flex-wrap: wrap; gap: .5rem .75rem; margin-bottom: .75rem; }
  .encounter-patient-context > span { background: var(--ui-secondary); border: 1px solid var(--ui-border); border-radius: 999px; color: var(--ui-muted-foreground); font-size: .7rem; font-weight: 600; padding: .2rem .55rem; text-transform: uppercase; }
  .encounter-patient-context small { color: var(--ui-muted-foreground); font-size: .8125rem; }
  .encounter-detail-workspace .encounter-patient-copy h2 { color: var(--ui-foreground); font-size: 1.125rem; font-weight: 600; line-height: 1.5; margin: 0; overflow-wrap: anywhere; }
  .encounter-patient-copy h2 small { color: var(--ui-muted-foreground); font-size: .75rem; font-weight: 500; margin-left: .35rem; }
  .encounter-detail-workspace .encounter-patient-id { color: var(--ui-muted-foreground); font-size: .8125rem; margin: .35rem 0 0; overflow-wrap: anywhere; }
  .encounter-patient-actions { align-items: flex-end; display: flex; flex: 0 0 auto; flex-direction: column; gap: .75rem; }
  .encounter-patient-avatar { border: 1px solid var(--ui-border); border-radius: 50%; box-sizing: border-box; height: 4rem; object-fit: cover; width: 4rem; }
  .encounter-patient-initial { align-items: center; background: var(--ui-secondary); color: var(--ui-secondary-foreground); display: flex; font-size: 1.35rem; justify-content: center; }
  .encounter-patient-change { align-items: center; background: transparent; border: 1px solid var(--ui-border); border-radius: calc(var(--ui-radius) - 2px); color: var(--ui-muted-foreground); cursor: pointer; display: inline-flex; font: inherit; font-size: .75rem; gap: .35rem; min-height: 2rem; padding: .35rem .6rem; }
  .encounter-patient-change i { font-size: .875rem; }
  .encounter-patient-date, .encounter-patient-date > span { align-items: center; display: flex; flex-wrap: wrap; gap: .5rem; }
  .encounter-patient-date { color: var(--ui-muted-foreground); font-size: .75rem; margin-top: .75rem; }
  .encounter-patient-date > span > i { font-size: 1rem; }
  .encounter-detail-workspace .encounter-visit-date-picker { box-sizing: border-box; padding: 1.5rem !important; }
  .encounter-detail-workspace .encounter-visit-date-picker h3 { color: var(--ui-foreground); font-size: 1.125rem; font-weight: 600; margin: 0 0 .5rem; }
  .encounter-visit-date-picker > p { color: var(--ui-muted-foreground); font-size: .8125rem; margin: 0 0 1.25rem; }
  .encounter-visit-date-field { display: grid; gap: .5rem; max-width: 24rem; }
  .encounter-visit-date-field label { color: var(--ui-foreground); font-size: .75rem; position: static !important; transform: none !important; }
  .encounter-detail-workspace .encounter-visit-date-field input { background: var(--ui-background) !important; border: 1px solid var(--ui-input) !important; border-radius: calc(var(--ui-radius) - 2px) !important; box-sizing: border-box; color: var(--ui-foreground); font: inherit; font-size: .875rem; height: 2.75rem !important; margin: 0 !important; padding: 0 .75rem !important; width: 100% !important; }
  .encounter-diagnosis-summary { align-items: center; background: var(--ui-card); border: 1px solid var(--ui-border); border-radius: var(--ui-radius); display: flex; gap: 1rem; justify-content: space-between; padding: 1.25rem; }
  .encounter-diagnosis-label { color: var(--ui-muted-foreground); font-size: .75rem; }
  .encounter-diagnosis-summary p { color: var(--ui-foreground); font-size: .875rem; line-height: 1.6; margin: .35rem 0 0; }
  .encounter-detail-workspace :is(button, input):focus-visible { outline: 2px solid var(--ui-ring); outline-offset: 2px; }
  @media (max-width: 600px) { .encounter-detail-workspace .encounter-patient-summary { gap: 1rem; padding: 1.25rem; } .encounter-patient-actions { align-items: flex-end; } .encounter-patient-change span { display: none; } .encounter-patient-avatar { height: 3rem; width: 3rem; } }
}
.progress {
  margin-top: -15px;
}

.encounter-name-line,
.encounter-date-line {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.encounter-name-line {
  margin-bottom: 8px;
}

.encounter-date-line {
  margin-top: 28px;
}

.encounter-date-line .chip {
  margin: 0;
}

.encounter-card-action {
  align-items: center;
  background: #ffffff;
  border: 1px solid #d8e4e8;
  border-radius: 4px;
  box-shadow: none;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 34px;
  justify-content: center;
  height: 34px;
  min-height: 34px;
  padding: 0;
  transition: background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease,
    transform 0.18s ease;
  width: 34px;
}

.encounter-card-action i {
  font-size: 19px;
}

.encounter-card-action:hover,
.encounter-card-action:focus-visible {
  box-shadow: 0 10px 18px -13px rgba(38, 50, 56, 0.9);
  outline: 0;
  transform: translateY(-1px);
}

.encounter-change-patient {
  color: #c62828;
}

.encounter-change-patient:hover,
.encounter-change-patient:focus-visible {
  background: #ffebee;
  border-color: #e53935;
  color: #b71c1c;
}

.encounter-change-date {
  color: #9a5b00;
}

.encounter-change-date:hover,
.encounter-change-date:focus-visible {
  background: #ffecb3;
  border-color: #f9a825;
  color: #714200;
}

.encounter-action-card {
  background: transparent;
  border: 1px solid #d6e2e6;
  box-shadow: 0 8px 24px -22px rgba(38, 50, 56, 0.75);
  margin: 18px 0 0;
}

.encounter-action-card-content {
  background: #ffffff;
  box-shadow: none;
  margin: 0;
  padding: 22px 24px;
}

.encounter-visit-actions,
.encounter-visit-primary-actions {
  align-items: center;
  display: flex;
  gap: 12px;
}

.encounter-visit-actions {
  justify-content: space-between;
}

.encounter-visit-primary-actions {
  justify-content: flex-end;
}

.encounter-visit-button {
  align-items: center;
  border: 1px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  gap: 9px;
  height: 52px;
  justify-content: center;
  letter-spacing: 0;
  line-height: 1;
  padding: 0 22px;
  text-transform: none;
  transition: background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease,
    color 0.18s ease, transform 0.18s ease;
  white-space: nowrap;
}

.encounter-visit-button i {
  font-size: 21px;
  line-height: 1;
}

.encounter-visit-button:hover:not(:disabled),
.encounter-visit-button:focus-visible:not(:disabled) {
  box-shadow: 0 10px 20px -14px rgba(38, 50, 56, 0.8);
  outline: 0;
  transform: translateY(-1px);
}

.encounter-visit-button:focus-visible {
  outline: 2px solid #00897b;
  outline-offset: 2px;
}

.encounter-visit-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.encounter-visit-cancel {
  background: #ffffff;
  border-color: #ef9a9a;
  color: #c62828;
}

.encounter-visit-cancel:hover:not(:disabled),
.encounter-visit-cancel:focus-visible:not(:disabled) {
  background: #fff5f5;
  border-color: #e53935;
}

.encounter-visit-save {
  background: #00897b;
  color: #ffffff;
  min-width: 164px;
}

.encounter-visit-save:hover:not(:disabled),
.encounter-visit-save:focus-visible:not(:disabled) {
  background: #00796b;
}

.encounter-visit-auth {
  background: #263238;
  color: #ffffff;
  min-width: 206px;
}

.encounter-visit-auth:hover:not(:disabled),
.encounter-visit-auth:focus-visible:not(:disabled) {
  background: #162329;
}

.encounter-request-section.is-workspace-ready {
  background: #ffffff;
  border: 1px solid #c9d7dc;
  box-shadow: 0 10px 22px -24px rgba(38, 50, 56, 0.7);
  margin: 0 0 20px;
  position: relative;
}

.encounter-request-head {
  border-bottom: 1px solid #dce6e9;
}

.encounter-request-toggle {
  align-items: center;
  background: #ffffff;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  padding: 14px 18px;
  text-align: left;
  width: 100%;
}

.encounter-request-toggle:hover,
.encounter-request-toggle:focus-visible {
  background: #f8fbfb;
}

.encounter-request-toggle:focus-visible {
  outline: 2px solid #009688;
  outline-offset: -2px;
}

.encounter-request-title {
  align-items: center;
  display: flex;
  gap: 12px;
}

.encounter-request-icon {
  align-items: center;
  background: #fff8e1;
  color: #b77900;
  display: inline-flex;
  flex: 0 0 38px;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.encounter-request-copy {
  display: grid;
  gap: 2px;
}

.encounter-request-copy strong {
  color: #263238;
  font-weight: 700;
}

.encounter-request-copy small {
  color: #607d8b;
}

.encounter-request-chevron {
  color: #607d8b;
  flex: 0 0 auto;
  font-size: 24px;
  transition: color 0.18s ease;
}

.encounter-request-toggle:hover .encounter-request-chevron {
  color: #00897b;
}

.encounter-request-body {
  display: grid;
  grid-template-rows: 0fr;
  opacity: 0;
  pointer-events: none;
  transition: grid-template-rows 0.28s ease, opacity 0.2s ease, visibility 0s linear 0.28s;
  visibility: hidden;
}

.encounter-request-body.is-expanded {
  grid-template-rows: 1fr;
  opacity: 1;
  pointer-events: auto;
  transition-delay: 0s;
  visibility: visible;
}

.encounter-request-body-inner {
  min-height: 0;
  overflow: hidden;
}

.encounter-request-body.is-expanded .encounter-request-body-inner {
  overflow: visible;
}

.encounter-request-body .encounter-item-stack {
  gap: 0 !important;
  padding: 0 !important;
}

.encounter-items-card {
  position: relative;
  scroll-margin-top: 210px;
}

.encounter-items-card .collection-item,
.encounter-approvals-card .collection-item,
.encounter-item-copy {
  text-align: left;
}

.encounter-item-copy {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
}

.encounter-items-card .secondary-content,
.encounter-approvals-card .secondary-content {
  flex: 0 0 auto;
}

.progress.encounter-submit-progress {
  border-radius: 0 0 4px 4px;
  bottom: -4px;
  height: 4px;
  left: 0;
  margin: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  width: 100%;
  z-index: 2;
}

.spacer5 {
  padding-top: 5px;
  padding-bottom: 5px;
}

.spacerTop10 {
  padding-top: 10px;
  padding-bottom: 0px;
}

.edit-black-item:hover .material-icons.black-text,
.edit-black-item:hover .material-icons.grey-text::before {
  color: rgb(21, 21, 206) !important;
}

.edit-item:hover .material-icons.grey-text,
.edit-item:hover .material-icons.grey-text::before {
  color: rgb(21, 21, 206) !important;
}

.cancel-item:hover .material-icons.grey-text,
.cancel-item:hover .material-icons.grey-text::before {
  color: rgb(214, 16, 16) !important;
}

@media (max-width: 600px) {
  .encounter-action-card-content {
    padding: 16px;
  }

  .encounter-visit-actions,
  .encounter-visit-primary-actions {
    align-items: stretch;
    flex-direction: column-reverse;
    width: 100%;
  }

  .encounter-visit-primary-actions {
    flex-direction: column;
  }

  .encounter-visit-button,
  .encounter-visit-save,
  .encounter-visit-auth {
    min-width: 0;
    width: 100%;
  }

  .encounter-request-toggle {
    padding: 12px 14px;
  }

  .encounter-request-copy small {
    display: none;
  }
}

</style>

<style>
.datepicker-modal {
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0 24px 56px rgba(25, 35, 42, 0.34);
  left: 50% !important;
  margin: 0 !important;
  max-height: 84vh;
  max-width: calc(100vw - 32px);
  position: fixed !important;
  right: auto !important;
  top: 50% !important;
  transform: translate(-50%, -50%) !important;
  z-index: 10060 !important;
}

#confirmSubmit.encounter-confirm-modal {
  background: #ffffff;
  border: 1px solid #dbe5e9;
  border-radius: 6px;
  box-shadow: 0 24px 56px rgba(25, 35, 42, 0.34);
  left: 50% !important;
  margin: 0 !important;
  max-height: 72vh;
  max-width: calc(100vw - 32px);
  position: fixed !important;
  right: auto !important;
  top: 50% !important;
  transform: translate(-50%, -50%) !important;
  width: min(680px, calc(100vw - 32px)) !important;
  z-index: 10060 !important;
}

.encounter-modal-host {
  position: relative;
  z-index: 10060;
}

.modal-overlay {
  background: rgba(20, 31, 38, 0.62) !important;
  bottom: 0 !important;
  height: 100vh !important;
  left: 0 !important;
  position: fixed !important;
  right: 0 !important;
  top: 0 !important;
  width: 100vw !important;
  z-index: 10050 !important;
}
</style>
